import InputItem from './InputItem'
import UserAppBar from './UserAppBar'
import { useHistory } from 'react-router'
import { useLocation } from 'react-router'
import { useEffect, useState } from 'react'
import EndServiceDialog from './EndServiceDialog'
import { IInput } from '../../../types/templates'
import { BlobProvider } from '@react-pdf/renderer'
import SaveServiceDialog from './SaveServiceDialog'
import ShareDocumentDialog from './ShareDocumentDialog'
import { TemplateGenerator } from './TemplateGenerator'
import DialogLink from '../../../components/DialogLink'
import { numberFormat } from '../../../utils/functions'
import { getPayment } from '../../../services/payments'
import { updateService } from '../../../services/services'
import FilePreviewer from '../../../components/FilePreviewer'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import Alert, { ActionsOptType } from '../../../components/Alert'
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons'
import { IInputValue, ITemplate, ITemplateData } from '../../../types/templates'
import { IService, IServiceData, SERVICE_STATUS } from '../../../types/services'
import { Button, Container, Grid, MobileStepper, Typography } from '@material-ui/core'

// Define IDialogEnd interface
const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(4),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    color: '#FFFFFF',
    backgroundColor: theme.palette.secondary.dark,
  },
  stepper: {
    background: 'transparent',
    paddingBottom: theme.spacing(4),
  },
}))

/**
 * Custom `TemplateEditor` component declaration.
 * This component is used to 
 * @return TemplateEditor
*/
const TemplateEditor = (): JSX.Element => {
  const classes = useStyles()
  const theme = useTheme()
  const history = useHistory()
  const location: any = useLocation()
  const loadData = ((): { template: ITemplate; service?: IService } => {
    if (location.state.origin)
      return {
        template: location.state.template as ITemplate,
        service: location.state.service as IService,
      }
    else return { template: location.state as ITemplate }
  })()
  const template = loadData.template
  let service = loadData.service
  const templateData = (template?.template_data || {
    blocks: [],
    inputs: [],
  }) as ITemplateData
  const [base64File, setBase64File] = useState<string>()
  const [inputValues, setInputValues] = useState<IInputValue[]>(
    service?.service_data ? service.service_data.inputValues : []
  )
  const [inputValue, setInputValue] = useState<IInputValue | undefined>(() => {
    const inputValue = inputValues.find((i: IInputValue) => i.pos === 0)
    return inputValue
  })
  const [activeStep, setActiveStep] = useState(0)
  const [openDialog, setOpenDialog] = useState(false)
  const [openEndDialog, setOpenEndDialog] = useState(false)
  const [surveyDialogOpen, setSurveyDialogOpen] = useState(false)
  const [inputError, setInputError] = useState({ error: false, label: '' })
  const [firstDowload, setFirstDowload] = useState(
    service?.service_data ? service.service_data.first_download : false
  )
  const [alertOptions, setAlertOptions] = useState<ActionsOptType>({
    message: null,
    type: null,
    open: false,
  })
  const [currentService, setCurrentService] = useState<IService>();  
  const [watermark, setWatermark] = useState(
    service?.service_data ? service.service_data.watermark : true
  )
  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  const [dataShareTo, setDataShareTo] = useState<any[]>([]);
  const [diffDate, setDiffDate] = useState<number>(0)
  const [validatorConditional, setValidatorConditional] = useState(true)

  // get paykment_id to service
  useEffect(() => {
    if(service && service.payment_id)
    {
      validateDateService(service.payment_id)
    }
  }, [])

  // change watermark value
  useEffect(() => {
    if (service && watermark === false ) {
      onChangeService({
        service_data: { ...service.service_data, watermark: false },
      })
      service = {
        ...service,
        service_data: {
          ...service.service_data,
          watermark: false,
        } as IServiceData,
      }
    }
    if (service && watermark !== false ) {
      onChangeService({
        service_data: { ...service.service_data, watermark: true },
      })
      service = {
        ...service,
        service_data: {
          ...service.service_data,
          watermark: true,
        } as IServiceData,
      }
    }
  }, [watermark])

  // cahge firstDowload value  
  useEffect(() => {
    if (service && !service?.service_data?.first_download && firstDowload) {
      onChangeService({
        service_data: { ...service.service_data, firstDowload: true },
      })
      service = {
        ...service,
        service_data: {
          ...service.service_data,
          first_download: true,
        } as IServiceData,
      }
    }
  }, [firstDowload])

  /**
   * This method is used to find the next or previous input valid
   * @param {String} type
   * @Return {void}
  */
  const findNextInputConditional = (type: string) => {
    let nextInput
    if(type === 'add')
    {
      nextInput = templateData.inputs.findIndex((i: IInput, index: number) => index > activeStep && !i.conditional)
      if(nextInput && nextInput < 0)
      { 
        setActiveStep(templateData.inputs.length - 1)
      }
      else if(nextInput && nextInput >= 0)
      {
        validateConditionalStep('add', nextInput-1)
      }
    }
    else if(type === 'sub')
    {
      for (let index = 1; index < templateData.inputs.length; index++) {
        const element = templateData.inputs[activeStep - index];
        if(!element.conditional)
        {
          nextInput = element
          setActiveStep(activeStep - index)
          return
        }
      }
    }
  }  

  /**
   * This is used to redirect the app.
  */
  const goPayment = () => {
    history.push({pathname: '/payment', state: { template, currentService: service } }) 
  }

  /**
   * This method is used to validate and find the next input according to the templateData.
   * @param {String} type
   * @param {Number} currentStep
   * @Return {void}
  */
  const validateConditionalStep = (
    type: 'add' | 'sub',
    currentStep: number
  ) => {
    setValidatorConditional(true)
    const step = currentStep + (type === 'add' ? 1 : -1)
    const inputEffect = templateData.inputs[step]

    if(!inputEffect)
    {
      if(step >= templateData.inputs.length) setValidatorConditional(false)
      findNextInputConditional(type)
      return
    }

    if (inputEffect.conditional) {
      // Improve multiple validation
      const cond: any = inputEffect.conditional
        .replace('{', '')
        .replace('}', '')
        .split('=')

      const inputValue = inputValues.find(
        (i: IInputValue) => i.pos === Number(cond[0]) - 1
      )
      if (inputValue) {
        let value = ''
        if (inputValue.type === 'select') {
          const options = inputValue.options?.split(',') || ''
          if (options && options.length > 0) {
            value = options[Number(inputValue.value)].trim()
          }
        } else value = inputValue.value.trim()
        if (value !== cond[1]) {
          //validar si hay un paso mas
          validateConditionalStep(type, step)
        } else {
          setActiveStep(step)
          const nextInput = templateData.inputs[step]
          const inputFind = inputValues.find(
            (i: IInputValue) => i.uid === nextInput.uid
          )
          if (inputFind) {
            setInputValue(inputFind)
          } else {
            setInputValue(undefined)
          }
        }
      } else {
        // console.error('INPUT MAL CONFIGURADO', inputEffect)
        validateConditionalStep(type, step)
      }
    } else {
      setActiveStep(step)
      const nextInput = templateData.inputs[step]
      const inputFind = inputValues.find(
        (i: IInputValue) => i.uid === nextInput.uid
      )
      if (inputFind) {
        setInputValue(inputFind)
      } else {
        setInputValue(undefined)
      }
    }
  }

  /**
   * This method is used to open newServiceDialog
   * @Return {void}
  */
  const openNewServiceDialog = () => {
    setOpenDialog(true)
  }

  /**
   * This method is used to open EndServiceDialog
   * @Return {void}
  */
  const openEndServiceDialog = () => {
    setOpenEndDialog(true)
  }

  /**
   * This method is used to open ShareDialog
   * @Return {void}
  */
  const openShareDialog = () => {
    setShareDialogOpen(true)
  }
  
  /**
   * This method is used to add information to array about the person to whom the document will be shared.
   * @param {String} email
   * @param {String} uid
   * @Return {void}
  */
  const arrayPeople = (email: string, uid: string) => {
    if(email !== '' || email !== undefined) 
    {
      const currentPeople = [...dataShareTo]
      if(currentPeople.length === 0 && email !== '')
      {
        currentPeople.push([email, uid, false])
      }
      else if(currentPeople.length > 0)
      {
        let response = false
        dataShareTo.forEach(element => {
          if(element[0] === email && element[1] === uid) 
          {
            response = true          
            return
          }
          if(element[0].includes([email]))
          {
            const val = currentPeople.indexOf(element)
            if(val > -1)
            {
              currentPeople.splice(val, 1)
            }
            if(element[1].includes([uid]))
            {
              if(element[0] !== email)
              {
                element[0] = email
              }
            }
          }
          if(element[1].includes([uid]))
          {
            if(element[0] !== email)
            {
              element[0] = email
            }
            response = true
          }          
        });
        
        if(response === false)
        {
          currentPeople.push([email, uid, false])            
        }              
      }
      setDataShareTo(currentPeople)
    }
  }
 
  /**
   * This method is used to save the value of the current step
   * validate the value of the input and current step
   * save the information depending on whether the service is new or already exists
   * @param {String} email
   * @param {String} uid
   * @Return {void}
  */ 
  const handleNext = async () => {
    if(inputValue?.value === undefined &&  templateData.inputs[activeStep].obligatory === true)
    {
      setAlertOptions({
        message: 'Este campo es obligatorio, por favor ingrese información válida para poder continuar',
        open: true,
        type: 'error',
      })
      return;
    }
    if(inputValue?.type === "email" && typeof inputValue.value === 'string')
    {
      arrayPeople(inputValue.value, inputValue.uid) 
    }
    if (activeStep === 0)
    { 
      if(service && location.state.origin)
      {
        const { error } = await updateService({
          service_id: service.id,
          ...service,
          service_data: {
            name: service?.service_data?.name,
            inputValues: inputValues,
            template_id: service?.service_data?.template_id,
          } as IServiceData,
        })
        if (error) setAlertOptions({ message: error, open: true, type: 'error' }) 
        validateConditionalStep('add', activeStep) 
      }
      else
      {
        if(!currentService)
        {
          openNewServiceDialog()  
        }
        else
        {
          validateConditionalStep('add', activeStep)
        }
      }
    }
    else if (activeStep === templateData.inputs.length - 1)
    {
      if(service && location.state.origin)
      {
        const { error } = await updateService({
          service_id: service.id,
          ...service,
          service_data: {
            name: service?.service_data?.name,
            inputValues: inputValues,
            template_id: service?.service_data?.template_id,
            finished_document: true,
            first_download: false,
            watermark: true
          } as IServiceData,
        })
        if (error) setAlertOptions({ message: error, open: true, type: 'error' }) 
      }
      if (currentService && currentService.service_data) 
      {
        const { error } = await updateService({
          service_id: currentService.id,
          ...currentService,
          service_data: {
            name: currentService.service_data.name,
            inputValues: inputValues,
            template_id: currentService.service_data.template_id,
            finished_document: true,
            first_download: false,
            watermark: true
          } as IServiceData,
        })
        if (error) setAlertOptions({ message: error, open: true, type: 'error' }) 
      }
      if(dataShareTo.length > 0)
      {   
        openShareDialog()     
      }
      else
      {
        openEndServiceDialog()
      }
    }
    else
    {
      if(service && location.state.origin)
      {
        const { error } = await updateService({
          service_id: service.id,
          ...service,
          service_data: {
            name: service?.service_data?.name,
            inputValues: inputValues,
            template_id: service?.service_data?.template_id,
          } as IServiceData,
        })
        if (error) setAlertOptions({ message: error, open: true, type: 'error' }) 
      }
      if (currentService && currentService.service_data) 
      {              
       const { error } = await updateService({
        service_id: currentService.id,
          ...currentService,
          service_data: {
            name: currentService.service_data.name,
            inputValues: inputValues,
            template_id: currentService.service_data.template_id,
          } as IServiceData,
        })
      if (error) setAlertOptions({ message: error, open: true, type: 'error' }) 
      }
      validateConditionalStep('add', activeStep)  
    }    
  }

  /**
   * This method is used to change the value of currentService and call the validateConditionalStep method
   * @Return {void}
  */
  const onServiceCreated = (newService:IService) => {
    setCurrentService(newService)
    validateConditionalStep('add', activeStep)  
  }

  /**
   * This method is used to return to a previous valid input
   * @Return {void}
  */
  const handleBack = () => {
    setInputError({ error: false, label: '' })
    const inputFind = inputValues.find(
      (i: IInputValue) => i.uid === templateData.inputs[activeStep - 1].uid
    )
    if (inputFind) {
      setInputValue(inputFind)
    }
    validateConditionalStep('sub', activeStep)
    // setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  /**
   * This method is used to update the service
   * @Return {void}
  */
  const onChangeService = async (updatedFields: any, message?: string) => {
    if (service) {
      const { data, error } = await updateService({
        service_id: service.id,
        ...service,
        ...updatedFields,
      })
      if (data && message)
        setAlertOptions({
          message,
          open: true,
          type: 'info',
        })
      if (error) setAlertOptions({ message: error, open: true, type: 'error' })
    }
  }

  /**
   * This method is used to change the value of watermark
   * @Return {void}
  */
  const changeStateWatermark = () => {
    setWatermark(!watermark) 
  }

  /**
   * This method is used to calculate the difference between payment date and creation date
   * @Return {void}
  */
  const validateDateService = async (paymentId: any) => {
    const { data, error } = await getPayment(paymentId)
    if(data)
    {
      const currentDate = new Date().getTime()
      const createdDate = new Date(data.payment_date).getTime()
      const diff = (currentDate - createdDate);
      setDiffDate(diff/(1000*60*60*24))
    }
    if (error) setAlertOptions({ message: error, open: true, type: 'error' })
  }

  return (
    <>
      <SaveServiceDialog
        template={template}
        open={openDialog}
        setOpen={setOpenDialog}
        inputValues={inputValues}
        onServiceCreated={onServiceCreated}
      />
      <EndServiceDialog
        open={openEndDialog}
        template={template}
        setOpen={setOpenEndDialog}
        currentService={currentService}
      />
      <ShareDocumentDialog
        open={shareDialogOpen}
        setOpen={setShareDialogOpen}
        people={dataShareTo} 
        idService={ currentService ? currentService.id : service?.id }
        user_id={ currentService ? currentService.user_id : service?.user_id }
      />
      <Alert alertOptions={alertOptions} setAlertOptions={setAlertOptions} />
      <DialogLink
        open={surveyDialogOpen}
        setOpen={setSurveyDialogOpen}
        title={'DANOS TU OPINION'}  
        t1={'POR SUPUESTO'}
        t2={'POR AHORA NO'}
        description={
          '¡Tu opinión es muy importante para nosotros! Eres nuestra prioridad. \n Te agradecemos si puedes regalarnos unos minutos de tu tiempo para evaluar nuestros servicio.'
        }
        link={
          'https://docs.google.com/forms/d/e/1FAIpQLSf7WTq4AylN1C7J0ChJVetpNK1286YlCNtlPlDaa2ldLlrvaQ/viewform?usp=pp_url'
        }
      />
      <UserAppBar />
      <Container className={classes.container} maxWidth="xl">
        <BlobProvider document={TemplateGenerator(template, inputValues, watermark, service ? service : currentService)}>
          {({ blob, error }) => {
            if (!error) {
              const reader = new FileReader()
              if (blob) {
                reader.readAsDataURL(blob)
                reader.onloadend = function () {
                  const base64data = reader.result
                  if (base64data) {
                    setBase64File(
                      base64data
                        .toString()
                        .replace('data:application/pdf;base64,', '')
                    )
                  }
                }
              }

              return <div></div>
            }
          }}
        </BlobProvider>

        <Grid container alignContent={'center'} justifyContent={'center'} spacing={4}>
          <Grid item xs={12} sm={12} lg={6}>
            <Typography
              variant="subtitle1"
              align="center"
              color="primary"
              paragraph
              style={{
                marginBottom: '3vh',
                fontWeight: 'bold',
                fontSize: '1.4rem',
              }}
            >
              {service?.service_data
                ? service.service_data.name
                : template.name}
            </Typography>
            <Typography
              variant="subtitle2"
              align="justify"
              color="textSecondary"
              paragraph
              style={{ marginBottom: '25px', whiteSpace: 'pre-line' }}
            >
              {template.description}
            </Typography>

            {((inputValue?.value === undefined && templateData.inputs[activeStep].obligatory === true) &&
              <>
                <Typography
                  variant="subtitle2"
                  align="justify"
                  color="textSecondary"
                  paragraph
                  style={{ marginLeft: '2.2vh', whiteSpace: 'pre-line', marginBottom: '5px' }}
                >
                  Campo obligatorio*
                </Typography>
              </>            
            )}
            {(service &&
                service.service_status_id === SERVICE_STATUS.PAID && diffDate > 3 ) && (
              <Typography
              variant="h6"
              align="center"
              color="textSecondary"
              paragraph
              style={{ marginBottom: '35px', whiteSpace: 'pre-line' }}
              >
                El tiempo máximo de edición después de realizar el pago ha expirado, 
                para realizar nuevos cambios por favor comuníquese con nuestros asesores por los diferentes canales de Justiexpress.
              </Typography> 
            )}

            {/* PASO A PASO  Editar DOCTLIST*/}
            {((service && 
              service.service_data?.finished_document !== true && location.state.origin) ||
              (service &&
                service.service_status_id === SERVICE_STATUS.PAID && diffDate <= 3 )) && (
              <>
                <MobileStepper
                  variant="progress"
                  steps={templateData.inputs.length}
                  position="static"
                  activeStep={activeStep}
                  className={classes.stepper}
                  nextButton={
                    <Button
                      size="small"
                      onClick={handleNext}
                      disabled={inputError.error}
                    >
                      {activeStep === templateData.inputs.length - 1
                        ? 'FINALIZAR'
                        : 'SIGUIENTE'}
                      {theme.direction === 'rtl' ? (
                        <KeyboardArrowLeft />
                      ) : (
                        <KeyboardArrowRight />
                      )}
                    </Button>
                  }
                  backButton={
                    <Button
                      size="small"
                      onClick={handleBack}
                      disabled={activeStep === 0}
                    >
                      {theme.direction === 'rtl' ? (
                        <KeyboardArrowRight />
                      ) : (
                        <KeyboardArrowLeft />
                      )}
                      ATRÁS
                    </Button>
                  }
                />
                {validatorConditional === true && activeStep <= templateData.inputs.length - 1 && (
                  <InputItem
                    inputError={inputError}
                    setInputError={setInputError}
                    inputsConfig={{ inputValue, setInputValue }}
                    pos={activeStep}
                    input={templateData.inputs[activeStep]}
                    inputValues={inputValues}
                    setInputValues={setInputValues}
                  />
                )}
              </>
            )}

            {(!service ) && (
              <>
                <MobileStepper
                  variant="progress"
                  steps={templateData.inputs.length}
                  position="static"
                  activeStep={activeStep}
                  className={classes.stepper}
                  nextButton={
                    <Button
                      size="small"
                      onClick={handleNext}
                      disabled={
                        (service &&
                         activeStep === templateData.inputs.length - 1) ||
                         inputError.error
                      }
                    >
                      {activeStep === templateData.inputs.length - 1
                        ? 'FINALIZAR'
                        : 'SIGUIENTE'}
                      {theme.direction === 'rtl' ? (
                        <KeyboardArrowLeft />
                      ) : (
                        <KeyboardArrowRight />
                      )}
                    </Button>
                  }
                  backButton={
                    <Button
                      size="small"
                      onClick={handleBack}
                      disabled={activeStep === 0}
                    >
                      {theme.direction === 'rtl' ? (
                        <KeyboardArrowRight />
                      ) : (
                        <KeyboardArrowLeft />
                      )}
                      ATRÁS
                    </Button>
                  }
                />
                {validatorConditional === true && activeStep <= templateData.inputs.length - 1 && (
                  <InputItem
                    inputError={inputError}
                    setInputError={setInputError}
                    inputsConfig={{ inputValue, setInputValue }}
                    pos={activeStep}
                    input={templateData.inputs[activeStep]}
                    inputValues={inputValues}
                    setInputValues={setInputValues}
                  />
                )}
              </>
            )}
            {activeStep >= templateData.inputs.length - 1 && validatorConditional === false && (
              <>
                <Typography
                  component="p"
                  variant="body1"
                  align="center"
                  color="textPrimary"
                  gutterBottom
                  style={{ fontSize: '0.9rem', fontWeight: 'normal', marginTop: '1.3rem' }}
                >
                  Ya no tienes que diligenciar más información, por favor presiona finalizar para culminar la creación de tu documento.
                </Typography>
              </>
            )}
            {service && template && service.service_status_id !== SERVICE_STATUS.PAID && (
              <>
                <Typography
                  component="p"
                  variant="body1"
                  align="center"
                  color="textPrimary"
                  gutterBottom
                  style={{ fontSize: '0.9rem', fontWeight: 'normal', marginTop: '1.3rem' }}
                >
                  ¡Tu documento está listo, te invitamos a realizar el pago para poder descargar y/o editar el documento $ {numberFormat(Number(template.price))} pesos! 
                </Typography>
                <Typography
                  component="p"
                  variant="body1"
                  align="center"
                  color="textPrimary"
                  gutterBottom
                  style={{ fontSize: '0.9rem', fontWeight: 'normal', marginTop: '1.5rem' }}
                >
                  Realiza tu pago de forma segura en PayU.
                </Typography>
                <Button
                  style={{ marginTop: '1.3rem'}}
                  type="submit"
                  color="primary"
                  fullWidth
                  variant="contained"
                  className={classes.submit}
                  onClick={goPayment}
                >
                  Pagar
                </Button>
              </>
            )}
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            lg={6}
            style={{
              display: 'flex',
              justifyContent: 'center',
              maxHeight: '100vh',
              marginBottom: '50vh'
            }}
          >
            {base64File && (
              <FilePreviewer
                hideDownload={
                  service && 
                  service.service_status_id === SERVICE_STATUS.PAID && 
                  service.service_data?.finished_document === true
                    ? false
                    : true
                }
                changeStateWatermark={changeStateWatermark}
                firstDowload={firstDowload}
                setSurveyDialogOpen={setSurveyDialogOpen}
                setFirstDowload={setFirstDowload}
                file={{
                  data: base64File,
                  mimeType: 'application/pdf',
                  name: `${service?.service_data?.name}.pdf`, // for download
                }}
              />
            )}
          </Grid>
        </Grid>
      </Container>
    </>
  )
}

export default TemplateEditor